import React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { CSSCursor, CSSObjectFit } from '../types/css'
import { BaseProps, BorderProps } from '../types/props'
import { getBorderStyle } from '../util/props/border'
import { isUndefined } from '../util/typechecks'

const shapeClipPathLookups = {
  circle: 'circle(50% at 50% 50%)',
  rectangle: '', // No clip-path equals a square
  rhombus: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  pentagon: 'polygon(50% 0%, 100% 40%, 80% 100%, 20% 100%, 0% 40%)',
  hexagon: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
  hexagonFlatTop: 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)',
  octagon:
    'polygon(30% 0, 70% 0, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0 70%, 0 30%)',
  kite: 'polygon(50% 0%, 100% 30%, 50% 100%, 0% 30%)',
}

export type ThumbnailShape =
  | 'circle'
  | 'rectangle'
  | 'rhombus'
  | 'pentagon'
  | 'hexagon'
  | 'hexagonFlatTop'
  | 'octagon'
  | 'kite'

interface WrapperProps {
  shadow?: string
}
const Wrapper = styled.div<WrapperProps>`
  display: inline-block;

  ${props =>
    props.shadow &&
    `
    filter: drop-shadow(${props.shadow});
  `};
`

interface ThumbnailImageProps extends BorderProps {
  shape: ThumbnailShape
  width: string
  height: string
  objectFit: CSSObjectFit
  borderRadius?: string
  cursor: CSSCursor
}

const ThumbnailImage = styled.img<ThumbnailImageProps>`
  width: ${props => props.width};
  height: ${props => props.height};
  object-fit: ${props => props.objectFit};
  clip-path: ${props => shapeClipPathLookups[props.shape]};

  ${getBorderStyle}
  border-radius: ${props => props.borderRadius};
  cursor: ${props => props.cursor};
`

export interface ThumbnailProps extends BorderProps, BaseProps {
  src: string
  alt?: string
  href?: string | { pathname: string; state?: object }

  shape?: ThumbnailShape
  /** Used to specify width AND height if these are not given. */
  size?: string
  /** The width of the thumbnail. Size will be used in its place if not specified. */
  width?: string
  /** The height of the thumbnail. Size will be used in its place if not specified. */
  height?: string
  objectFit?: CSSObjectFit
  borderRadius?: string
  /**
   * Shadow as used in filter: drop-shadow
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/filter-function/drop-shadow
   */
  shadow?: string
  cursor?: CSSCursor

  onClick?: (event: React.MouseEvent<HTMLImageElement>) => void
}

export const Thumbnail: React.FC<ThumbnailProps> = ({
  className,
  id,

  src,
  alt = 'thumnail',
  href,

  shape = 'circle',
  size = '38px',
  width,
  height,
  objectFit = 'cover',
  border,
  borderWidth,
  borderStyle,
  borderColor,
  borderRadius = '9999px',
  shadow,
  cursor = 'auto',

  onClick = () => void 0,
}) => {
  const history = useHistory()

  function handleClick(evt: React.MouseEvent<HTMLImageElement>) {
    onClick(evt)
    if (evt.defaultPrevented || isUndefined(href)) return

    if (typeof href === 'string') history.push(href)
    else history.push(href.pathname, { state: href.state })
  }

  return (
    <Wrapper className={className} id={id} shadow={shadow}>
      <ThumbnailImage
        src={src}
        alt={alt}
        shape={shape}
        width={width ?? size}
        height={height ?? size}
        objectFit={objectFit}
        border={border}
        borderWidth={borderWidth}
        borderStyle={borderStyle}
        borderColor={borderColor}
        borderRadius={borderRadius}
        cursor={cursor}
        onClick={handleClick}
      />
    </Wrapper>
  )
}
